import Vue from 'vue'
import Vuetify from 'vuetify/lib'
import { UserVuetifyPreset } from 'vuetify'
import { camelCase } from 'lodash'
import { baseVueI18n } from './vue-i18n'
import {
  BaseError,
  BaseErrorChain,
  BaseField,
  BaseFieldConstraint,
  BaseForm,
  BaseHook,
  BaseNotification,
  BaseTableEntities,
  BaseTableModules,
  BaseUserModule
} from '@/types'
import { loadModuleConfigs } from '@/utils/config-util'
import { createHooks } from '@/utils/hook-util'

interface TableData {
  moduleName: string
  entityName: string
  fields: BaseField[]
}

const VUETIFY_APP_TITLE: string = process.env.VUE_APP_TITLE
const VUETIFY_SUPPORT_CONSOLE_LOGGING = (process.env.NODE_ENV !== 'production')

const themeDarkHooks = createHooks<boolean>()
const notificationHooks = createHooks<BaseNotification>()

export default class BaseVuetify extends Vuetify {
  private title: string
  private notificationCount: number
  private tableModules: BaseTableModules

  constructor(vuetifyPreset: Partial<UserVuetifyPreset> = {}) {
    vuetifyPreset.lang = {
      t: (key, ...args) => baseVueI18n.t(`app.${key.substring(1)}`, args) as string
    }

    super(vuetifyPreset)

    this.title = VUETIFY_APP_TITLE
    this.notificationCount = 0
    this.tableModules = {}
  }

  get $tables(): BaseTableModules {
    return this.tableModules
  }

  setDefaultModuleNames(defaultModuleNames: string[]): void {
    this.loadModuleTables(defaultModuleNames)
  }

  setUserModules(userModules: BaseUserModule[]): void {
    this.loadModuleTables(userModules.map(({ moduleName }) => moduleName))

    this.generateDynamicTables(userModules)
  }

  setUserModule(userModule: BaseUserModule): void {
    const { head } = document
    const { light, dark } = this.framework.theme.themes
    const { title, moduleBarColor, modulePrimaryColor } = userModule

    this.setTitle(title)

    head.querySelector<HTMLElement>('meta[name=theme-color]')?.setAttribute('content', moduleBarColor)
    head.querySelector<HTMLElement>('meta[name=msapplication-TileColor]')?.setAttribute('content', moduleBarColor)
    head.querySelector<HTMLElement>('meta[name=apple-mobile-web-app-status-bar-style]')?.setAttribute('content', moduleBarColor)
    head.querySelector<HTMLElement>('link[rel=mask-icon]')?.setAttribute('color', moduleBarColor)
    head.querySelector<HTMLElement>('link[rel=icon][href="img/icons/favicon.svg"]')?.remove()

    light.primary = modulePrimaryColor
    light.accent = modulePrimaryColor
    dark.primary = modulePrimaryColor
    dark.accent = modulePrimaryColor
  }

  setLanguage(language: string): void {
    document.documentElement.lang = language

    this.setTitle()
  }

  setThemeDark(themeDark: boolean): void {
    const { theme } = this.framework

    if (themeDark === theme.dark) return

    theme.dark = themeDark

    themeDarkHooks.execute(theme.dark)
  }

  setNotifications(notifications: BaseNotification[]): void {
    this.setTitle(undefined, notifications.length)
  }

  registerThemeDarkHook(themeDarkHook: BaseHook<boolean>): void {
    themeDarkHooks.register(themeDarkHook)
  }

  registerNotificationHook(notificationHook: BaseHook<BaseNotification>): void {
    notificationHooks.register(notificationHook)
  }

  $rules(ruleKeys: string[]): (boolean | string)[] { //TODO: definir tipo rule
    const ruleValidators: any[] = []//TODO: definir tipo validator

    ruleKeys.forEach((ruleKey) => {
      let ruleValidator = null

      const [ruleName, ruleValue] = ruleKey.split('=')
      const message = baseVueI18n.t(`app.rule.${ruleName}`, [ruleValue]) as string

      if (ruleName === 'required') ruleValidator = (value: any) => ((!!value) || message)
      else if (ruleName === 'min') ruleValidator = (value: any) => ((value && (value.length >= ruleValue)) || message)
      else if (ruleName === 'max') ruleValidator = (value: any) => ((value && (value.length <= ruleValue)) || message)
      else if (ruleName === 'email') ruleValidator = (value: any) => (/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(value) || message)
      // eslint-disable-next-line no-console
      else console.warn('Rule not implemented:', ruleName)

      if (ruleValidator) ruleValidators.push(ruleValidator)
    })

    return ruleValidators
  }

  $notify(notification: BaseNotification): void {
    notificationHooks.execute({
      ...notification,
      temporary: notification.temporary ?? true,
      autoremove: notification.autoremove ?? true
    })
  }

  $error(error: BaseError, form?: BaseForm): void {
    const { data, status } = error.response ?? {}
    const errorChain: BaseErrorChain[] = data?.length
      ? data
      : [{
        errorName: error.message ?? error.name,
        errorMessage: error.message
      }]
    const fallbackMessageCode = `app.error.status${status ?? 0}`

    errorChain.forEach((chainedError) => {
      const { errorName, errorMessage, fieldName } = chainedError
      const messageCode = `app.error.${camelCase(errorName)}`
      const title = this.getErrorMessage(messageCode, fallbackMessageCode, errorMessage)
      const input = fieldName ? form?.inputs.find((input) => input.$attrs.name === fieldName) : null

      if (input) input.errorBucket.push(() => title)
      else {
        this.$notify({
          title,
          color: 'red darken-1'
        })
      }
    })
  }

  private loadModuleTables(moduleNames: string[]): void {
    loadModuleConfigs<BaseTableEntities>('/tables', moduleNames, ({ moduleName, moduleValue }) => {
      this.tableModules[moduleName] = moduleValue
    })
  }

  private generateDynamicTables(userModules: BaseUserModule[]): void {
    userModules.forEach((userModule) => {
      const { moduleName, controllers } = userModule

      let tableEntities = this.tableModules[moduleName]

      controllers.forEach((controller) => {
        const { entityName, fields } = controller

        tableEntities = tableEntities ?? {}

        this.mergeTables(tableEntities, {
          moduleName,
          entityName,
          fields
        })
      })

      if (tableEntities) this.tableModules[moduleName] = tableEntities
    })
  }

  private mergeTables(tableEntities: BaseTableEntities, tableData: TableData): void {
    const { moduleName, entityName, fields } = tableData

    let tableHeaders = tableEntities[entityName]

    fields?.forEach((field, index) => {
      const {
        name,
        type,
        multiple,
        transient,
        auditing,
        searchPath,
        subnames,
        enums,
        style,
        pattern,
        timeZone,
        currency,
        unit,
        minimumIntegerDigits,
        minimumFractionDigits,
        constraints
      } = field

      const required = !!constraints?.includes(BaseFieldConstraint.NOT_NULL)
      const insertable = !constraints?.includes(BaseFieldConstraint.NOT_INSERT)
      const updatable = !constraints?.includes(BaseFieldConstraint.NOT_UPDATE)

      const text = auditing ? `app.auditing.field.${name}` : `${moduleName}.${entityName}.field.${name}`
      const chips = multiple
      const sortable = !(multiple || transient)
      const align = (type === 'number') ? 'end' : 'start'
      const width = multiple ? '33%' : undefined

      const isDisabled = (inserting: boolean): boolean => (auditing || ((!insertable) && inserting) || ((!updatable) && (!inserting)))

      let formatter!: (value: any) => any
      if (subnames) {
        const objectFormat = (objectValue: any): string => objectValue
          ? subnames.map((subname) => objectValue[subname]).filter((subvalue) => subvalue).join(' | ')
          : objectValue

        formatter = (value) => (multiple && Array.isArray(value)) ? value.map((subvalue) => objectFormat(subvalue)) : objectFormat(value)
      }
      else if (enums) {
        const enumFormat = (enumValue: any): string => enumValue
          ? (baseVueI18n.t(`${moduleName}.${entityName}.value.${name}.${enumValue}`) as string)
          : enumValue

        formatter = (value) => (multiple && Array.isArray(value)) ? value.map((subvalue) => enumFormat(subvalue)) : enumFormat(value)
      }
      else if (type && ['datetime-local', 'date', 'time'].includes(type)) {
        const [style1, style2] = style?.includes('-and-') ? style.split('-and-') : [style, style]

        const dateStyle = (type === 'time') ? undefined : (style1 || 'short') as Intl.DateTimeFormatOptions['dateStyle']
        const timeStyle = (type === 'date') ? undefined : (style2 || 'medium') as Intl.DateTimeFormatOptions['timeStyle']

        const datetimeFormat = (datetimeValue: any): string => ((datetimeValue !== undefined) && (datetimeValue !== null))
          ? (pattern ? baseVueI18n.df(new Date(datetimeValue), { pattern, timeZone }) : baseVueI18n.d(new Date(datetimeValue), { dateStyle, timeStyle, timeZone }))
          : datetimeValue

        formatter = (value) => (multiple && Array.isArray(value)) ? value.map((subvalue) => datetimeFormat(subvalue)) : datetimeFormat(value)
      }
      else if (type && (type === 'number')) {
        const numberStyle = (style || 'decimal') as Intl.NumberFormatOptions['style']
        const unitDisplay = (numberStyle !== 'unit') ? undefined : 'narrow' as Intl.NumberFormatOptions['unitDisplay']

        const numberFormat = (numberValue: any): string => ((numberValue !== undefined) && (numberValue !== null))
          ? baseVueI18n.n(numberValue, { style: numberStyle, currency, unit, unitDisplay, minimumIntegerDigits, minimumFractionDigits })
          : numberValue

        formatter = (value) => (multiple && Array.isArray(value)) ? value.map((subvalue) => numberFormat(subvalue)) : numberFormat(value)
      }

      tableHeaders = tableHeaders ?? []

      const tableHeader = tableHeaders.find((tableHeader) => tableHeader.value === name)
      if (tableHeader) {
        tableHeader.text = ((tableHeader.text ?? '') !== '') ? tableHeader.text : text
        tableHeader.index = tableHeader.index ?? index
        tableHeader.type = tableHeader.type ?? type
        tableHeader.multiple = tableHeader.multiple ?? multiple
        tableHeader.chips = tableHeader.chips ?? chips
        tableHeader.auditing = tableHeader.auditing ?? auditing
        tableHeader.required = tableHeader.required ?? required
        tableHeader.searchPath = tableHeader.searchPath ?? searchPath
        tableHeader.subnames = tableHeader.subnames ?? subnames
        tableHeader.enums = tableHeader.enums ?? enums
        tableHeader.style = tableHeader.style ?? style
        tableHeader.pattern = tableHeader.pattern ?? pattern
        tableHeader.timeZone = tableHeader.timeZone ?? timeZone
        tableHeader.currency = tableHeader.currency ?? currency
        tableHeader.unit = tableHeader.unit ?? unit
        tableHeader.minimumIntegerDigits = tableHeader.minimumIntegerDigits ?? minimumIntegerDigits
        tableHeader.minimumFractionDigits = tableHeader.minimumFractionDigits ?? minimumFractionDigits
        tableHeader.sortable = tableHeader.sortable ?? sortable
        tableHeader.align = tableHeader.align ?? align
        tableHeader.width = tableHeader.width ?? width
        tableHeader.isDisabled = tableHeader.isDisabled ?? isDisabled
        tableHeader.formatter = tableHeader.formatter ?? formatter
      }
      else {
        tableHeaders.push({
          value: name,
          text,
          index,
          type,
          multiple,
          chips,
          auditing,
          required,
          searchPath,
          subnames,
          enums,
          style,
          pattern,
          timeZone,
          currency,
          unit,
          minimumIntegerDigits,
          minimumFractionDigits,
          sortable,
          align,
          width,
          isDisabled,
          formatter
        })
      }
    })

    if (tableHeaders) {
      tableHeaders.sort((sourceTableHeader, targetTableHeader) => {
        const sourceTableHeaderIndex = sourceTableHeader.index ?? 0
        const targetTableHeaderIndex = targetTableHeader.index ?? 0

        return sourceTableHeaderIndex - targetTableHeaderIndex
      })

      tableEntities[entityName] = tableHeaders
    }
  }

  private getErrorMessage(messageCode: string, fallbackMessageCode: string, defaultMessage?: string): string {
    let message = baseVueI18n.t(messageCode) as string
    if (message === messageCode) message = baseVueI18n.t(fallbackMessageCode) as string
    if ((message === fallbackMessageCode) && defaultMessage) message = defaultMessage

    if (VUETIFY_SUPPORT_CONSOLE_LOGGING) {
      // eslint-disable-next-line no-console
      console.log('Error message:', messageCode, '|', fallbackMessageCode, defaultMessage ? `| ${defaultMessage}` : '')
    }

    return message
  }

  private setTitle(title?: string, notificationCount?: number): void {
    if (title) this.title = title
    else title = this.title
    if (notificationCount !== undefined) this.notificationCount = notificationCount
    else notificationCount = this.notificationCount

    title = baseVueI18n.t(title) as string
    title = (title !== VUETIFY_APP_TITLE) ? `${title} - ${VUETIFY_APP_TITLE}` : title
    title = notificationCount ? `(${notificationCount}) ${title}` : title

    document.title = title
  }
}

Vue.use(BaseVuetify)

export const baseVuetify = new BaseVuetify()

declare module 'vue/types/vue' {
  interface Vue {
    readonly $tables: typeof BaseVuetify.prototype.$tables
    $rules: typeof BaseVuetify.prototype.$rules
    $notify: typeof BaseVuetify.prototype.$notify
    $error: typeof BaseVuetify.prototype.$error
  }
}

Vue.prototype.$tables = baseVuetify.$tables
Vue.prototype.$rules = (rules: string[]): (boolean | string)[] => baseVuetify.$rules(rules)
Vue.prototype.$notify = (notification: BaseNotification): void => baseVuetify.$notify(notification)
Vue.prototype.$error = (error: BaseError, form?: BaseForm): void => baseVuetify.$error(error, form)